import { useTranslations } from "use-intl";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ActivityIcon } from "lucide-react";
import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Badge } from "@/components/catalyst/badge";
import { Link } from "@/components/catalyst/link";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useIncidentRecord } from "@/app/Incidents/Hooks/useIncidentRecord";
import { useIncidentActions } from "@/app/Incidents/Hooks/useIncidentActions";
import { IncidentStatusBadge } from "@/app/Incidents/Components/IncidentStatusBadge";

export function IncidentRecordScreen() {
  const t = useTranslations("incident");
  const incident = useIncidentRecord();
  const actions = useIncidentActions();
  const { isAdmin } = useTenant();

  return (
    <RecordScreen
      tabParams={{ incidentId: incident.id }}
      title={incident.reference}
      actionsGroups={actions(incident)}
      badges={
        <>
          <IncidentStatusBadge
            status={incident.status}
            outcome={incident.closed_outcome}
          />
          <Badge color="zinc">{t(`type.${incident.type}`)}</Badge>
          {isAdmin ? (
            <Badge color="zinc">{incident.supplier.name}</Badge>
          ) : null}
          <Badge color="zinc">
            <Link
              className="underline"
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
              params={(prev) => ({ ...prev, id: incident.workOrder.id })}
            >
              {incident.workOrder.reference}
            </Link>
          </Badge>
        </>
      }
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents"
                params={(prev) => prev}
              >
                {t("breadcrumb.incidents")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{incident.reference}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      tabs={[
        {
          name: t("tabs.details"),
          Icon: DocumentTextIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents/$incidentId",
        },
        {
          name: t("tabs.activity"),
          Icon: ActivityIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents/$incidentId/activity",
        },
      ]}
    />
  );
}
